import { ChangeEventHandler, useCallback, useState } from 'react';
import { Divider, Flex, Heading } from '@chakra-ui/layout';
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AutocompleteInput,
  Button,
  CustomIcon,
  FormFooter,
  MaskInput,
  Pagination,
  PasswordInput,
  Popover,
  SelectInput,
  DateInput,
  StatusTag,
  Switch,
  SwitchField,
  TextField,
  TextInput,
  Unform,
  RadioInput,
} from 'components';
import { SubmitHandler } from '@unform/core';
import { exampleValidationSchema } from 'yupSchemas/__example__';
import { ICreateUserForm } from 'types/test';
import { Table } from 'components/Table';
import { openNotification } from 'utils/notification';
import { debounce } from 'utils/debounce';

export const Components = () => {
  const sample = '22/12/2021';
  const initialDateInput = { date: sample, radio_value: '' };
  const handleSubmit = useCallback<SubmitHandler<ICreateUserForm>>((data: any) => {}, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [percent, setPercent] = useState<string>('0');

  const handleGraph = useCallback(v => {
    setPercent(v);
  }, []);

  const handleSearch = useCallback<ChangeEventHandler<HTMLInputElement>>(e => {}, []);

  return (
    <Flex minHight="100vh" alignItems="center" direction="column" padding="2rem" background="white">
      <Flex direction="column" maxW="60rem" width="100%" color="black" gridGap="1rem">
        <Flex direction="column" gridGap="1rem">
          <Heading size="lg">Table component</Heading>

          <Divider />

          <Table
            enabled={[true]}
            id={[0]}
            row={[0]}
            headers={['Um', 'Dois', 'Três', 'Quatro']}
            sizes={[1, 3, 1, 1]}
            fullLoading={false}
            rows={[
              ['Um', 'Dois', 'Três', 'Quatro'],
              ['Um', 'Dois', 'Três', 'Quatro'],
              ['Um', 'Dois', 'Três', 'Quatro'],
              ['Um', 'Dois', 'Três', 'Quatro'],
              ['Um', 'Dois', 'Três', 'Quatro'],
            ]}
            pagination={{
              pagesCount: 10,
              onChange: newPage => {
                console.log(newPage);
              },
            }}
          />
        </Flex>

        <Flex direction="column" gridGap="1rem">
          <Heading size="lg">Form (Open console to see form data)</Heading>

          <Divider />

          <Unform
            onSubmit={handleSubmit}
            validationSchema={exampleValidationSchema}
            initialData={initialDateInput}
          >
            <Flex>
              <Heading size="md" mb="1rem">
                Text input
              </Heading>
            </Flex>

            <Flex gridGap="1rem">
              <Flex flex={2}>
                <TextInput name="name" placeholder="Nome do cliente" label="Nome" />
              </Flex>

              <Flex flex={1}>
                <TextInput name="surname" placeholder="Sobre nome do cliente" label="Sobrenome" />
              </Flex>
            </Flex>

            <Flex>
              <Heading size="md" mb="1rem">
                Password Input
              </Heading>
            </Flex>

            <Flex gridGap="1rem" maxW="40rem">
              <Flex flex={1}>
                <PasswordInput name="password" placeholder="Senha" label="Senha" />
              </Flex>

              <Flex flex={1}>
                <PasswordInput
                  name="confirmPassword"
                  placeholder="Confirmar senha"
                  label="Confirmar senha"
                />
              </Flex>
            </Flex>

            <Flex>
              <Heading size="md" mb="1rem">
                With icon
              </Heading>
            </Flex>

            <Flex gridGap="1rem">
              <Flex flex={1}>
                <TextInput leftIcon="user" name="user" placeholder="User" label="User" />
              </Flex>

              <Flex flex={1}>
                <TextInput
                  rightIcon={{ name: 'home', color: 400, clickable: true }}
                  name="home"
                  placeholder="Home"
                  label="Home"
                />
              </Flex>
            </Flex>

            <Flex>
              <Heading size="md" mb="1rem">
                With mask
              </Heading>
            </Flex>

            <Flex gridGap="1rem">
              <Flex flex={1}>
                <MaskInput
                  mask="phone"
                  name="maskPhone"
                  placeholder="Mask pone"
                  label="Mask phone"
                />
              </Flex>
              <Flex flex={1}>
                <MaskInput mask="digit" name="digitNumber" placeholder="0-9" label="Digit" />
              </Flex>

              <Flex flex={1}></Flex>

              <Flex flex={1}>
                <MaskInput
                  mask="number"
                  name="maskNumber"
                  placeholder="Mask Number"
                  label="Mask Number"
                />
              </Flex>

              <Flex flex={1}>
                <MaskInput
                  mask="cpf"
                  name="maskCpf"
                  placeholder="Mask Cpf"
                  label="Mask Cpf"
                  cleanValue
                />
              </Flex>

              <Flex flex={1}>
                <MaskInput
                  mask="currency"
                  name="maskCurrency"
                  placeholder="Mask Currency"
                  label="Mask Currency"
                />
              </Flex>
            </Flex>

            <Flex>
              <Heading size="md" mb="1rem">
                Select
              </Heading>
            </Flex>

            <Flex gridGap="1rem">
              <Flex flex={1}>
                <SelectInput
                  label="Select"
                  name="select"
                  options={[
                    { label: 'Option 1', value: 1 },
                    { label: 'Option 2', value: 2 },
                    { label: 'Option 3', value: 3 },
                  ]}
                />
              </Flex>

              <Flex flex={1}>
                <SelectInput
                  label="Select With Dynamic Prop"
                  name="selectWithDynamicProp"
                  optionLabel="name"
                  optionValue="id"
                  options={[
                    { name: 'Option 1', id: 1 },
                    { name: 'Option 2', id: 2 },
                    { name: 'Option 3', id: 3 },
                  ]}
                />
              </Flex>
            </Flex>

            <Flex>
              <Heading size="md" mb="1rem">
                Switch
              </Heading>
            </Flex>

            <Flex gridGap="1rem">
              <Switch name="switch" label="Switch boolean" />

              <Switch
                name="switchWithValue"
                label="Switch with value"
                value={{ id: '1', name: 'name' }}
              />
            </Flex>

            <Flex>
              <Heading size="md" mt="1rem">
                Radio
              </Heading>
            </Flex>

            <Flex mt="1rem">
              <RadioInput name="radio_value" />
            </Flex>

            <Flex>
              <Heading size="md" mb="1rem">
                Other variants (Chakra UI)
              </Heading>
            </Flex>

            <Flex gridGap="1rem">
              <TextInput
                name="variantFilled"
                placeholder="Variant Filled"
                label="Variant Filled"
                variant="filled"
              />

              <TextInput
                name="variantFlushed"
                placeholder="Variant flushed"
                label="Variant flushed"
                variant="flushed"
              />
            </Flex>

            <Flex>
              <Heading size="md" mb="1rem">
                Autocomplete
              </Heading>
            </Flex>

            <Flex>
              <AutocompleteInput
                name="autocomplete"
                label="Teste"
                // onSelectOption={val => console.log(val)}
                options={[
                  { label: 'One', value: 1 },
                  { label: 'Two', value: 2 },
                  { label: 'Three', value: 3 },
                ]}
              />
            </Flex>
            <Flex flexDirection="column">
              <Heading size="md" mb="1rem">
                Calendar
              </Heading>
              <DateInput name="date" />
            </Flex>
            <Button type="submit">Submit</Button>
          </Unform>
        </Flex>

        <Flex direction="column" gridGap="1rem">
          <Heading size="lg">Buttons</Heading>

          <Divider />

          <Flex gridGap="1rem" justifyContent="center">
            <Button>Primary (default)</Button>

            <Button outline>Primary - outline</Button>

            <Button variant="cancel">Cancel</Button>

            <Button variant="cancel" outline>
              Cancel - outline
            </Button>

            <Button variant="danger">Danger</Button>

            <Button variant="danger" outline>
              Danger - outline
            </Button>
          </Flex>

          <Flex gridGap="1rem">
            <Flex maxW="20rem" flex={1}>
              <Button fullWidth>Primary - Full width</Button>
            </Flex>

            <Flex>
              <Button leftIcon="edit" outline>
                Primary with icon
              </Button>
            </Flex>

            <Flex>
              <Button isLoading>Primary with loading</Button>
            </Flex>

            <Flex>
              <Button isLoading variant="danger" outline>
                Danger outline with loading
              </Button>
            </Flex>
          </Flex>
        </Flex>

        <Flex direction="column" gridGap="1rem">
          <Heading size="lg">Notification</Heading>

          <Divider />

          <Flex gridGap="1rem" justifyContent="center">
            <button onClick={() => openNotification('Success message')}>Success (default)</button>
            <button onClick={() => openNotification('Info message', 'info')}>Info</button>
            <button onClick={() => openNotification('Warn message', 'warn')}>Warn</button>
            <button onClick={() => openNotification('Error message', 'error')}>Error</button>
          </Flex>
        </Flex>

        <Flex direction="column" gridGap="1rem">
          <Heading size="lg">Debounce with Input (with root Input component, without Form)</Heading>

          <Divider />
          <Heading size="md">Open console to see data</Heading>

          <Flex maxW="50rem" gridGap="1rem">
            <TextField
              leftIcon={{ name: 'search', color: 1100 }}
              placeholder="Without debounce"
              label="Without debounce"
              onChange={handleSearch}
            />

            <TextField
              leftIcon={{ name: 'search', color: 1100 }}
              placeholder="With debounce"
              label="With debounce"
              onChange={debounce(handleSearch)}
            />
          </Flex>
        </Flex>

        <Flex direction="column" gridGap="1rem">
          <Heading size="lg">Status tag</Heading>

          <Divider />

          <Flex gridGap="1rem">
            <Flex maxW="5rem" flex={1}>
              <StatusTag variant="accept">Finalizado</StatusTag>
            </Flex>

            <Flex maxW="5rem" flex={1}>
              <StatusTag variant="danger">Cancelado</StatusTag>
            </Flex>

            <Flex maxW="5rem" flex={1}>
              <StatusTag variant="warn">Pendente</StatusTag>
            </Flex>

            <Flex maxW="5rem" flex={1}>
              <StatusTag variant="info">Em curso</StatusTag>
            </Flex>

            <Flex maxW="8rem" flex={1}>
              <StatusTag variant="notDefined">Não iniciado</StatusTag>
            </Flex>

            <Flex maxW="5rem" flex={1}>
              <StatusTag variant="notDefined">Overflow ellipsis</StatusTag>
            </Flex>
          </Flex>
        </Flex>

        <Flex direction="column" gridGap="1rem">
          <Heading size="lg">Popover</Heading>

          <Divider />

          <Flex gridGap="1rem">
            <Popover
              rows={[
                { label: 'Primeira opção', onClick: () => alert('Click first option') },
                { label: 'Segunda opção', onClick: () => alert('Click second option') },
                {
                  label: 'Terceira opcao desabilitado',
                  onClick: () => alert('Click second option'),
                  disabled: true,
                },
              ]}
            />

            <Popover
              rows={[
                {
                  label: (
                    <Flex w="7rem" flex={1}>
                      <StatusTag variant="accept">Finalizado</StatusTag>
                    </Flex>
                  ),
                  onClick: () => alert('Click first option'),
                },
                {
                  label: (
                    <Flex w="7rem" flex={1}>
                      <StatusTag variant="danger">Cancelar</StatusTag>
                    </Flex>
                  ),
                  onClick: () => alert('Click second option'),
                },
                {
                  label: (
                    <Flex w="7rem" flex={1}>
                      <StatusTag variant="warn">Pendente</StatusTag>
                    </Flex>
                  ),
                  onClick: () => alert('Click thirty option'),
                },
                {
                  label: (
                    <Flex w="7rem" flex={1}>
                      <StatusTag variant="notDefined">Desabilitado</StatusTag>
                    </Flex>
                  ),
                  onClick: () => alert('Click thirty option'),
                  disabled: true,
                },
              ]}
            />
          </Flex>
        </Flex>

        <Flex direction="column" gridGap="1rem">
          <Heading size="lg">Accordion</Heading>

          <Divider />

          <Accordion>
            <AccordionHeader>Header no accordion</AccordionHeader>

            <AccordionBody>Body</AccordionBody>
          </Accordion>

          <Accordion>
            <AccordionHeader iconOnLeft>
              <Heading size="sm">Menu &quot;Avaliações&quot;</Heading>
            </AccordionHeader>

            <AccordionBody>
              <Flex marginTop="1rem" paddingBottom="1rem" width="100%" direction="column">
                <Flex bgColor="brand.1000" width="100%" p="1rem" borderRadius="8px">
                  <SwitchField label="Avaliação de Novos Projetos" />
                </Flex>

                <Flex width="100%" p="1rem 1rem" borderRadius="8px">
                  <SwitchField label="Registro de Operação de Tratamento de Dados" />
                </Flex>

                <Flex bgColor="brand.1000" width="100%" p="1rem 1rem" borderRadius="8px">
                  <SwitchField label="Teste de Legítimo Interesse" />
                </Flex>
              </Flex>
            </AccordionBody>
          </Accordion>
        </Flex>

        <Flex direction="column" gridGap="1rem">
          <Heading size="lg">Pagination</Heading>

          <Divider />

          <Pagination pagesCount={10} />
        </Flex>

        <Flex direction="column" gridGap="1rem">
          <Heading size="lg">FormFooter</Heading>

          <Divider />

          <FormFooter
            state="edit"
            editLabel="Excluir usuário"
            deleteLabel="Excluir usuário"
            deleteOnSaveStep
          />
        </Flex>

        <Flex direction="column" gridGap="1rem">
          <Heading size="lg">Base section</Heading>
          <Divider />
          content
        </Flex>
        <Flex gridGap="20px" color="#ac32cb" flexWrap="wrap">
          <CustomIcon color="#ff2323" position="top-left" icon="user_alt" size="30px" />
          <CustomIcon position="top" icon="user_gear_alt" size="30px" />
          <CustomIcon position="top-right" icon="document_alt" size="30px" />

          <CustomIcon position="center-left" icon="squares_alt" size="30px" />
          <CustomIcon position="center" icon="share_alt" size="30px" />
          <CustomIcon position="center-right" icon="bookmark_alt" size="30px" />

          <CustomIcon position="bottom-left" icon="home" size="30px" />
          <CustomIcon position="bottom" icon="home_alt" size="30px" />
          <CustomIcon position="bottom-right" icon="arrow_left" size="30px" />
          <CustomIcon $noSphere icon="arrow_left" size="30px" />
        </Flex>
      </Flex>
    </Flex>
  );
};
